import { useCallback, useEffect, useState } from 'react';
import styles from './InstallPrompt.module.css';

// The `beforeinstallprompt` event is not part of the standard DOM lib types.
interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

// True when the app is already running as an installed PWA, in which case no
// install affordance should be shown.
function isStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const displayMode = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari exposes standalone on the navigator instead of matchMedia.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return Boolean(displayMode || iosStandalone);
}

// iOS Safari never fires `beforeinstallprompt`, so it needs a manual
// "Add to Home Screen" hint instead of a programmatic prompt.
function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isOtherBrowser = /crios|fxios|edgios|opios/i.test(ua);
  return isIos && !isOtherBrowser;
}

// A visible install affordance for the PitchVault PWA. On browsers that support
// `beforeinstallprompt` (Chrome, Edge, Android) it captures the deferred event
// and triggers the native prompt on click. On iOS Safari — which has no such
// event — it reveals manual Add-to-Home-Screen instructions instead.
export default function InstallPrompt(): JSX.Element | null {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [showIosHint, setShowIosHint] = useState<boolean>(false);

  const iosEligible = isIosSafari() && !installed;

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Prevent Chrome's mini-infobar so we can present our own button.
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setShowIosHint(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } finally {
        // The deferred event can only be used once.
        setDeferredPrompt(null);
      }
      return;
    }
    if (iosEligible) {
      setShowIosHint((open) => !open);
    }
  }, [deferredPrompt, iosEligible]);

  if (installed) {
    return null;
  }

  const canInstall = Boolean(deferredPrompt) || iosEligible;
  if (!canInstall) {
    return null;
  }

  return (
    <div className={styles.wrap}>
      {iosEligible && showIosHint && (
        <div className={styles.hint} role="status">
          Tap the Share icon, then <strong>Add to Home Screen</strong> to install PitchVault.
        </div>
      )}
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        aria-label="Install PitchVault app"
        aria-expanded={iosEligible ? showIosHint : undefined}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
        Install app
      </button>
    </div>
  );
}
